import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UsePaginationParams {
  defaultPage?: number;
  defaultLimit?: number;
}

interface UsePaginationReturn {
  currentPage: number;
  itemsPerPage: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  updateURL: (page: number, limit: number) => void;
}

export const usePagination = ({ 
  defaultPage = 1, 
  defaultLimit = 100 
}: UsePaginationParams = {}): UsePaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [itemsPerPage, setItemsPerPage] = useState(defaultLimit);

  useEffect(() => {
    // Read URL parameters on component mount
    const pageParam = searchParams.get('page');
    const limitParam = searchParams.get('limit');

    const urlPage = pageParam ? parseInt(pageParam, 10) : defaultPage;
    const urlLimit = limitParam ? parseInt(limitParam, 10) : defaultLimit;

    // Validate page and limit values
    const validPage = Math.max(1, urlPage);
    const validLimit = [10, 25, 50, 100, 200].includes(urlLimit) ? urlLimit : defaultLimit;

    setCurrentPage(validPage);
    setItemsPerPage(validLimit);

    // Update URL if the parsed values are different from defaults
    if (validPage !== urlPage || validLimit !== urlLimit) {
      updateURL(validPage, validLimit);
    }
  }, [searchParams, defaultPage, defaultLimit]);

  const updateURL = (page: number, limit: number) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (page === 1) {
      newParams.delete('page');
    } else {
      newParams.set('page', page.toString());
    }
    
    if (limit === defaultLimit) {
      newParams.delete('limit');
    } else {
      newParams.set('limit', limit.toString());
    }

    setSearchParams(newParams, { replace: true });
  };

  const setPage = (page: number) => {
    setCurrentPage(page);
    updateURL(page, itemsPerPage);
  };

  const setLimit = (limit: number) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing limit
    updateURL(1, limit);
  };

  return {
    currentPage,
    itemsPerPage,
    setPage,
    setLimit,
    updateURL,
  };
};
